import styled from 'styled-components';
import { Container } from '../../components/Grid/Container';
import { Typography } from '../../components/Typography';
import { Card } from '../../components/Card/Card';
import { 
  FaLinkedin, 
  FaGithub,
  FaReact, 
  FaNode, 
  FaAws, 
  FaPython,
  FaJava,
  FaDocker,
  FaDatabase,
  FaLaravel,
  FaPhp,
  FaFileContract
} from 'react-icons/fa';
import { 
  SiTypescript,
  SiKubernetes,
  SiTerraform,
  SiGooglecloud,
  SiAngular,
  SiVuedotjs,
  SiPhp,
  SiCplusplus,
  SiDotnet,
  SiGo,
  SiPython,
  SiJest,
  SiGithub,
  SiBitbucket,
  SiSelenium,
  SiCucumber,
  SiCypress,
  SiPostman,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiRedis,
  SiElasticsearch,
  SiAmazoneks,
  SiAmazonecs,
  SiAwslambda,
  SiCloudflare,
  SiNewrelic,
  SiDatadog,
  SiGrafana,
  SiGooglebigquery,
  SiApacheairflow,
  SiRabbitmq,
  SiJira,
  SiConfluence,
  SiMiro,
  SiFigma,
  SiAdobexd,
  SiSketch
} from 'react-icons/si';
import {
  TbSql
} from 'react-icons/tb';
import React from 'react';
import { AthenaIcon } from '../../components/Icons/AthenaIcon';

const AboutSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background: ${({ theme }) => theme.colors.background.dark};
  position: relative;
  overflow: hidden;
  scroll-margin-top: 60px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.effects.circuitPattern};
    opacity: 0.05;
  }
`;

const AboutContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AboutText = styled.div`
  h2 {
    font-size: ${({ theme }) => theme.typography.sizes['3xl']};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary.light} 0%,
      ${({ theme }) => theme.colors.primary.main} 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: ${({ theme }) => theme.typography.sizes.lg};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  }
`;

const ValueCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const ValueCard = styled.div`
  background: ${({ theme }) => theme.effects.glassMorphism};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary.main};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const TeamSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background: ${({ theme }) => theme.colors.background.darker};
  scroll-margin-top: 60px;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled(Card)`
  text-align: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-8px);
  }
`;

const ProfileImage = styled.div<{ $adjustPosition?: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  overflow: hidden;
  border: 3px solid ${({ theme }) => theme.colors.primary.main};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${props => props.$adjustPosition ? 'center 3%' : 'center 25%'};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  a {
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: color 0.2s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary.main};
    }
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const TechIcon = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const TechRadarSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  background: ${({ theme }) => theme.colors.background.dark};
  scroll-margin-top: 60px;
`;

const RadarContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 3rem auto;
  aspect-ratio: 1;

  @media (max-width: 768px) {
    max-width: 100%;
    margin: 2rem auto;
  }
`;

const RadarCircle = styled.div<{ $level: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${props => (100 - props.$level * 20)}%;
  height: ${props => (100 - props.$level * 20)}%;
  border: 2px solid ${({ theme }) => theme.colors.primary.main}33;
  border-radius: 50%;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.primary.main}33;
    transform: rotate(45deg);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: ${({ theme }) => theme.colors.primary.main}33;
    transform: rotate(45deg);
  }
`;

const TechItem = styled.div<{ x: number; y: number }>`
  position: absolute;
  transform: translate(-50%, -50%);
  left: ${props => props.x}%;
  top: ${props => props.y}%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.2);
    z-index: 10;
  }

  svg {
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.primary.main};
  }
  
  span {
    font-size: ${({ theme }) => theme.typography.sizes.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-align: center;
    margin-top: 0.25rem;
    background: ${({ theme }) => theme.colors.background.darker}CC;
    padding: 0.25rem 0.5rem;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    svg {
      font-size: 1.2rem;
    }
    
    span {
      font-size: ${({ theme }) => theme.typography.sizes.xs};
      padding: 0.15rem 0.3rem;
    }
  }

  @media (max-width: 480px) {
    svg {
      font-size: 1rem;
    }
    
    span {
      font-size: ${({ theme }) => theme.typography.sizes.xs};
      padding: 0.1rem 0.2rem;
    }
  }
`;

const QuadrantLabel = styled.div<{ $quadrant: number }>`
  position: absolute;
  ${props => {
    switch (props.$quadrant) {
      case 0: return 'top: 5%; right: 5%; text-align: right;'; // Backend
      case 1: return 'top: 5%; left: 5%; text-align: left;'; // Frontend
      case 2: return 'bottom: 5%; left: 5%; text-align: left;'; // Infraestrutura
      case 3: return 'bottom: 5%; right: 5%; text-align: right;'; // Dados
    }
  }}
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: bold;
  font-size: 1.2rem;
  padding: 10px;
  background: ${({ theme }) => theme.colors.background.darker}CC;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  z-index: 2;

  @media (max-width: 480px) {
    font-size: 0.9rem;
    ${props => {
      switch (props.$quadrant) {
        case 0: return 'top: 2%; right: 2%;';
        case 1: return 'top: 2%; left: 2%;';
        case 2: return 'bottom: 2%; left: 2%;';
        case 3: return 'bottom: 2%; right: 2%;';
      }
    }}
  }
`;

const teamMembers = [
  {
    name: "Adriano Fagundes",
    role: "Business Analyst & Requirements Engineer",
    image: "/images/team/adriano-fagundes.jpg",
    linkedin: "https://br.linkedin.com/in/adriano-tomaz-fagundes-6a2a797a",
    stack: [SiJira, SiConfluence, SiMiro, SiFigma]
  },
  {
    name: "Ana Emilia",
    role: "Digital Content Specialist & Social Media Strategist",
    image: "/images/team/ana-emilia.jpg",
    linkedin: "https://www.linkedin.com/in/anaemiliamauro",
    stack: [SiFigma, SiAdobexd, SiMiro, SiSketch]
  },
  {
    name: "Brenner Fagundes",
    role: "Chief Technology Officer & Solution Architect",
    image: "/images/team/brenner.jpg",
    linkedin: "https://br.linkedin.com/in/brenner-fagundes",
    github: "https://github.com/brenner-fagundes",
    stack: [FaAws, SiKubernetes, FaDocker, SiTerraform, FaNode, FaReact, SiTypescript]
  },
  {
    name: "Bryan Fagundes",
    role: "Software Engineer & AI Engineer",
    image: "/images/team/bryan.jpg",
    linkedin: "https://www.linkedin.com/in/bryanjfagundes/",
    stack: [FaPython, SiTypescript, FaNode, FaReact]
  },
  {
    name: "Camila Taborda",
    role: "Senior Software Engineer & Software Architecture Specialist",
    image: "/images/team/camila-taborda.jpg",
    linkedin: "https://br.linkedin.com/in/camila-taborda-38292089",
    stack: [FaNode, FaReact, SiTypescript, FaAws, SiKubernetes]
  },
  {
    name: "Charles Cleyton",
    role: "Software Engineer & Senior Contract Analyst",
    image: "/images/team/charles-cleyton.jpg",
    linkedin: "https://www.linkedin.com/in/charlescleyton/",
    stack: [FaLaravel, FaPhp, FaNode, FaDocker, FaFileContract, FaAws]
  },
  {
    name: "Cleison",
    role: "Software Engineer & Security Analyst",
    image: "/images/team/cleison.jpg",
    linkedin: "https://www.linkedin.com/in/cleisonss/",
    stack: [FaNode, SiKubernetes, FaAws, SiTerraform]
  },
  {
    name: "Cleiton Neves",
    role: "Software Engineer & Machine Learning Engineer",
    image: "/images/team/cleiton.jpg",
    linkedin: "https://www.linkedin.com/in/sncleiton/",
    stack: [FaPython, SiTypescript, FaNode, FaAws]
  },
  {
    name: "Gabriela Coutinho",
    role: "UX/UI Designer & Digital Transformation Specialist",
    image: "/images/team/gabriela-coutinho.jpg",
    linkedin: "https://br.linkedin.com/in/gabriela-coutinho-a78ab0157",
    stack: [SiFigma, SiAdobexd, SiSketch, SiMiro]
  },
  {
    name: "Guilherme Barbosa",
    role: "Cloud Architect & DevOps Engineering Lead",
    image: "/images/team/guilherme-coelho.jpg",
    linkedin: "https://br.linkedin.com/in/guilhermebarbosa2",
    stack: [FaAws, SiKubernetes, FaDocker, SiTerraform, SiGooglecloud]
  },
  {
    name: "Igor Soares",
    role: "Software Engineer & DevOps Specialist",
    image: "/images/team/igor-soares.jpg",
    linkedin: "https://www.linkedin.com/in/igorsoaressilveira/",
    stack: [FaDocker, SiKubernetes, FaAws, SiTerraform]
  },
  {
    name: "João Luiz",
    role: "Software Engineering Technical Leader & Cybersecurity Analyst",
    image: "/images/team/joao-luiz.jpg",
    linkedin: "https://www.linkedin.com/in/joaocaraujo/",
    stack: [FaNode, SiKubernetes, FaAws, SiTerraform]
  },
  {
    name: "João Victor",
    role: "Data Scientist & Data Engineer",
    image: "/images/team/joao-vitor.jpg",
    linkedin: "https://www.linkedin.com/in/joaovbelchi/",
    stack: [FaPython, SiPostgresql, SiMongodb, SiElasticsearch, SiApacheairflow]
  },
  {
    name: "Kaio Bitencourt",
    role: "Business Development & Sales Representative",
    image: "/images/team/kaio-bitencourt.jpg",
    linkedin: "https://www.linkedin.com/in/kaio-bitencourt-751721219/",
    stack: [SiJest, SiPostman, SiGithub]
  },
  {
    name: "Pedro Moura",
    role: "Senior Software Engineer & Prompt Engineer",
    image: "/images/team/pedro.jpg",
    linkedin: "https://www.linkedin.com/in/pedro-ciriaco-moura/",
    stack: [FaReact, FaNode, SiTypescript, FaPython]
  },
  {
    name: "Rafael Alkmim",
    role: "Software Architect & Cloud Engineer",
    image: "/images/team/rafael.jpg",
    linkedin: "https://www.linkedin.com/in/rafael-alkmim-21160819a/",
    stack: [FaAws, SiKubernetes, FaDocker, SiTerraform, SiGooglecloud]
  },

  {
    name: "Roberts Rodrigues",
    role: "Developer Full Stack",
    image:"/images/team/roberts.jpg",
    linkedin:"https://www.linkedin.com/in/roberts-ryse",
    stack:[FaNode, FaReact, SiTypescript, FaAws]
  },
  {
    name: "Roque Diego",
    role: "Data Architecture Lead & SEO Specialist & Prompt Engineer",
    image: "/images/team/roque.jpg",
    linkedin: "https://www.linkedin.com/in/roqueviana/",
    stack: [SiPostgresql, SiMongodb, SiElasticsearch, FaPython, SiGooglecloud, SiApacheairflow]
  },
  {
    name: "Silvanio Saldanha",
    role: "Quality Assurance Lead",
    image: "/images/team/silvanio-saldanha.jpg",
    linkedin: "https://www.linkedin.com/in/silvaniosaldanha/",
    stack: [SiJest, SiCypress, SiSelenium, SiCucumber]
  }
];

// Atualizar a definição do tipo TechItem
type TechItem = {
  icon: React.ComponentType | (() => React.ReactElement);
  name: string;
  distance: number;
  angle: number;
};

const radarTechnologies: Record<string, TechItem[]> = {
  // Frontend (45° a 135°)
  frontend: [
    { icon: FaReact, name: 'React', distance: 0.3, angle: 65 },
    { icon: SiAngular, name: 'Angular', distance: 0.5, angle: 95 },
    { icon: SiVuedotjs, name: 'Vue.js', distance: 0.4, angle: 115 }
  ],
  
  // Backend (315° a 45°)
  backend: [
    { icon: FaNode, name: 'Node.js', distance: 0.3, angle: 330 },
    { icon: FaJava, name: 'Java', distance: 0.4, angle: 350 },
    { icon: SiDotnet, name: '.NET', distance: 0.5, angle: 355 },
    { icon: SiPython, name: 'Python', distance: 0.5, angle: 15 },
    { icon: SiTypescript, name: 'TypeScript', distance: 0.45, angle: 25 },
    { icon: SiGo, name: 'Go', distance: 0.4, angle: 35 },
    { icon: SiPhp, name: 'PHP', distance: 0.35, angle: 40 }
  ],

  // Infraestrutura (135° a 225°)
  infra: [
    { icon: FaAws, name: 'AWS', distance: 0.35, angle: 145 },
    { icon: SiGithub, name: 'GitHub', distance: 0.45, angle: 155 },
    { icon: SiGrafana, name: 'Grafana', distance: 0.3, angle: 165 },
    { icon: SiGooglecloud, name: 'GCP', distance: 0.4, angle: 175 },
    { icon: SiDatadog, name: 'Datadog', distance: 0.35, angle: 185 },
    { icon: FaDocker, name: 'Docker', distance: 0.3, angle: 195 },
    { icon: SiKubernetes, name: 'Kubernetes', distance: 0.4, angle: 205 },
    { icon: SiNewrelic, name: 'New Relic', distance: 0.45, angle: 215 },
    { icon: SiTerraform, name: 'Terraform', distance: 0.5, angle: 220 }
  ],

  // Dados (225° a 315°)
  data: [
    { icon: SiMongodb, name: 'MongoDB', distance: 0.3, angle: 240 },
    { icon: SiPostgresql, name: 'PostgreSQL', distance: 0.4, angle: 260 },
    { icon: SiElasticsearch, name: 'Elasticsearch', distance: 0.5, angle: 275 },
    { icon: SiMysql, name: 'MySQL', distance: 0.5, angle: 290 },
    { icon: SiRedis, name: 'Redis', distance: 0.4, angle: 300 },
    { icon: AthenaIcon, name: 'Amazon Athena', distance: 0.35, angle: 282 },
    { icon: TbSql, name: '', distance: 0.2, angle: 273 },
    { icon: FaDatabase, name: 'SQL Server', distance: 0.2, angle: 265 }
  ],

  // Ferramentas (círculo interno)
  tools: [
    { icon: SiJest, name: 'Jest', distance: 0.15, angle: 135 },
    { icon: SiPostman, name: 'Postman', distance: 0.15, angle: 225 }
  ]
};

export const About = () => {
  return (
    <>
      <AboutSection id="about">
        <Container>
          <AboutContent>
            <AboutText>
              <h2>Quem Somos</h2>
              <p>
                Somos uma empresa de tecnologia focada em desenvolver soluções 
                inovadoras que transformam a maneira como as empresas operam 
                no mundo digital.
              </p>
              <p>
                Nossa missão é combinar expertise técnica com criatividade para 
                entregar resultados excepcionais que impulsionam o sucesso de 
                nossos clientes.
              </p>
            </AboutText>
            <div>
              <img 
                src="/images/about-image.svg" 
                alt="Ilustração Tech" 
                style={{ 
                  width: '100%', 
                  maxWidth: '500px',
                  filter: 'drop-shadow(0 0 10px rgba(0, 178, 255, 0.3))'
                }} 
              />
            </div>
          </AboutContent>

          <ValueCards>
            <ValueCard>
              <h3>Inovação</h3>
              <p>
                Buscamos constantemente novas tecnologias e soluções criativas 
                para os desafios dos nossos clientes.
              </p>
            </ValueCard>
            <ValueCard>
              <h3>Excelência</h3>
              <p>
                Comprometimento com a qualidade e as melhores práticas em 
                cada projeto que desenvolvemos.
              </p>
            </ValueCard>
            <ValueCard>
              <h3>Parceria</h3>
              <p>
                Construímos relacionamentos duradouros baseados em confiança 
                e resultados consistentes.
              </p>
            </ValueCard>
          </ValueCards>
        </Container>
      </AboutSection>
      
      <TechRadarSection id="tech">
        <Container>
          <Typography $variant="h2" $gradient $align="center">
            Radar de Tecnologias
          </Typography>
          <Typography $variant="body1" $color="secondary" $align="center">
            Tecnologias que dominamos e avaliamos constantemente
          </Typography>
          
          <RadarContainer>
            {[0, 1, 2, 3].map(level => (
              <RadarCircle key={level} $level={level} />
            ))}
            
            <QuadrantLabel $quadrant={0}>BACKEND</QuadrantLabel>
            <QuadrantLabel $quadrant={1}>FRONTEND</QuadrantLabel>
            <QuadrantLabel $quadrant={2}>INFRAESTRUTURA</QuadrantLabel>
            <QuadrantLabel $quadrant={3}>DADOS</QuadrantLabel>

            {Object.entries(radarTechnologies).map(([quadrant, techs]) =>
              techs.map((tech, index) => {
                // Converter ângulo para radianos e ajustar o offset
                const angleInRadians = (tech.angle) * Math.PI / 180;
                const x = 50 + Math.cos(angleInRadians) * tech.distance * 100;
                const y = 50 - Math.sin(angleInRadians) * tech.distance * 100;
                
                return (
                  <TechItem key={`${quadrant}-${index}`} x={x} y={y}>
                    {React.createElement(tech.icon)}
                    <span>{tech.name}</span>
                  </TechItem>
                );
              })
            )}
          </RadarContainer>
        </Container>
      </TechRadarSection>

      <TeamSection id="team">
        <Container>
          <Typography $variant="h2" $gradient $align="center">
            Nossa Equipe
          </Typography>
          <Typography $variant="body1" $color="secondary" $align="center">
            Profissionais experientes e apaixonados por tecnologia
          </Typography>
          
          <TeamGrid>
            {teamMembers.map((member, index) => (
              <TeamCard key={index} $variant="glass">
                <ProfileImage $adjustPosition={member.name === "Gabriela Coutinho"}>
                  <img src={member.image} alt={member.name} />
                </ProfileImage>
                <Typography $variant="h4" $gradient>
                  {member.name}
                </Typography>
                <Typography $variant="body2" $color="secondary">
                  {member.role}
                </Typography>
                
                <TechStack>
                  {member.stack.map((Icon, i) => (
                    <TechIcon key={i}>
                      <Icon />
                    </TechIcon>
                  ))}
                </TechStack>
                
                <SocialLinks>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                      <FaLinkedin size={20} />
                    </a>
                  )}
                  {member.github && (
                    <a href={member.github} target="_blank" rel="noopener noreferrer">
                      <FaGithub size={20} />
                    </a>
                  )}
                </SocialLinks>
              </TeamCard>
            ))}
          </TeamGrid>
        </Container>
      </TeamSection>
    </>
  );
}; 